import { HttpErrorResponse } from '@angular/common/http';

const FIELD_LABELS: Record<string, string> = {
  firstName: 'first name',
  lastName: 'last name',
  studentCode: 'student code',
  employeeCode: 'employee code',
  dateOfBirth: 'date of birth',
  dob: 'date of birth',
  joiningDate: 'joining date',
  dueDate: 'due date',
  startDate: 'start date',
  endDate: 'end date',
  startTime: 'start time',
  endTime: 'end time',
  branchId: 'branch',
  batchId: 'batch',
  studentId: 'student',
  courseId: 'course',
  teacherId: 'teacher',
  roleId: 'staff type',
  email: 'email',
  phone: 'phone number',
  alternatePhone: 'alternate phone',
  address: 'address',
  gender: 'gender',
  password: 'password',
  currentPassword: 'current password',
  newPassword: 'new password',
  confirmPassword: 'confirm password',
  name: 'name',
  code: 'code',
  amount: 'amount',
  transactionId: 'transaction ID',
  gstNo: 'GST number',
  panNo: 'PAN',
  pinCode: 'PIN code',
  interestedIn: 'interest',
  source: 'source',
  relation: 'relation',
  relationship: 'relation',
  remarks: 'notes',
  note: 'note',
  status: 'status',
};

const KNOWN_MESSAGES: Record<string, string> = {
  'student not found': 'We could not find that student.',
  'guardian not found': 'We could not find that guardian.',
  'enrollment not found': 'We could not find that enrollment.',
  'batch not found': 'We could not find that batch.',
  'course not found': 'We could not find that course.',
  'branch not found': 'We could not find that branch.',
  'invoice not found': 'We could not find that invoice.',
  'staff not found': 'We could not find that staff member.',
  'role not found': 'We could not find that staff type.',
  'guardian is already linked to this student':
    'That guardian is already linked to this student.',
  'cannot delete a batch with active enrollments':
    'This batch still has students enrolled. Remove them first, then try again.',
  'cannot delete a course that has batches assigned':
    'This course still has batches. Remove or reassign them first, then try again.',
  'enter a valid date.': 'Enter a valid date.',
  'a record with those details already exists.':
    'A record with those details already exists.',
  'this is linked to other records, so it cannot be saved or removed yet.':
    'This is linked to other records, so it cannot be saved or removed yet.',
  'we could not find that record.': 'We could not find that record.',
  'some details are not valid. please review and try again.':
    'Some details are not valid. Please review and try again.',
  'notes is required': 'Please add a follow-up note.',
  'cannot delete a guardian linked to students. unlink them first.':
    'This guardian is still linked to students. Unlink them first, then try again.',
  'you do not have permission to manage students':
    'You do not have permission to manage students.',
  'you do not have permission to manage enrollments':
    'You do not have permission to change enrollments.',
  'you can only manage students in your branch':
    'You can only work with students in your own branch.',
  'you can only view students in batches you teach':
    'You can only view students in batches you teach.',
  'you do not have access to this student':
    'You do not have access to this student.',
  'you must belong to a branch':
    'Your account is not assigned to a branch. Ask an administrator for help.',
  'branchid is required': 'Please select a branch.',
  'invalid email or password': 'Email or password is incorrect. Please try again.',
  'current password is incorrect': 'The current password is incorrect.',
  'schedule conflict detected':
    'That time clashes with another class. Change the time, room, or teacher.',
  'starttime must be before endtime': 'End time must be after start time.',
  'start time must be a 24-hour time like 16:00.':
    'Enter start time as 24-hour time, for example 16:00.',
  'end time must be a 24-hour time like 16:00.':
    'Enter end time as 24-hour time, for example 16:00.',
  'starttime and endtime are required when daysofweek is provided':
    'Please choose days and enter start and end times.',
  'internal server error':
    'Something went wrong on our side. Please try again in a moment.',
};

export interface UserFacingErrorOptions {
  /** Login form: 401 means wrong credentials, not an expired session. */
  login?: boolean;
}

export function userFacingError(
  error: unknown,
  fallback: string,
  options: UserFacingErrorOptions = {},
): string {
  if (!(error instanceof HttpErrorResponse)) {
    if (error instanceof Error && error.message && !looksTechnical(error.message)) {
      return error.message;
    }
    return fallback;
  }

  if (error.status === 0) {
    return 'We could not reach the server. Check your internet connection and try again.';
  }

  if (options.login && error.status === 405) {
    return 'Sign-in could not reach the API. Wait for the latest Pages deploy, then refresh.';
  }

  if (options.login && (error.status === 401 || error.status === 400)) {
    return 'Email or password is incorrect. Please try again.';
  }

  if (error.status === 401) {
    // Auth interceptor owns sign-out messaging for expired sessions.
    // Returning empty avoids a second misleading "session expired" toast
    // from page loaders while refresh/logout is already handled.
    return '';
  }

  if (error.status === 403) {
    return fromBody(error) ?? 'You do not have permission to do this.';
  }

  if (error.status === 404) {
    return fromBody(error) ?? fallback;
  }

  if (error.status === 409) {
    return fromBody(error) ?? 'This conflicts with an existing record. Please review and try again.';
  }

  if (error.status === 429) {
    return 'Too many attempts. Please wait a moment and try again.';
  }

  if (error.status === 400 || error.status === 422) {
    return fromValidation(error) ?? fromBody(error) ?? fallback;
  }

  if (error.status >= 500) {
    return fromBody(error) ??
      'Something went wrong on our side. Please try again. If it keeps happening, contact support.';
  }

  return fromBody(error) ?? fallback;
}

function fromBody(error: HttpErrorResponse): string | null {
  const body = error.error;
  if (body == null) {
    return null;
  }
  if (typeof body === 'string') {
    if (body.includes('<') || body.length > 180 || looksTechnical(body)) {
      return null;
    }
    return friendlyKnown(body);
  }
  if (typeof body !== 'object') {
    return null;
  }

  const raw = (body as { message?: unknown }).message;
  const text = normalizeMessage(raw);
  if (!text || looksTechnical(text)) {
    return null;
  }
  return friendlyKnown(text);
}

function fromValidation(error: HttpErrorResponse): string | null {
  const raw = (error.error as { message?: unknown } | null)?.message;
  const parts = Array.isArray(raw) ? raw : typeof raw === 'string' ? [raw] : [];
  const mapped = parts
    .map((item) => (typeof item === 'string' ? mapValidationLine(item) : null))
    .filter((item): item is string => Boolean(item));

  if (!mapped.length) {
    return null;
  }
  return [...new Set(mapped)].slice(0, 3).join(' ');
}

function mapValidationLine(line: string): string | null {
  const known = friendlyKnown(line);
  if (known !== line) {
    return known;
  }

  let match = /^property (\w+) should not exist$/i.exec(line);
  if (match) {
    return 'Some details are not valid. Please review the form and try again.';
  }

  match = /^(\w+) must be an email$/i.exec(line);
  if (match) {
    return 'Enter a valid email address.';
  }

  match = /^(\w+) should not be empty$/i.exec(line);
  if (match) {
    return `Please enter the ${labelFor(match[1])}.`;
  }

  match = /^(\w+) must be longer than or equal to (\d+) characters$/i.exec(line);
  if (match) {
    return `The ${labelFor(match[1])} is too short.`;
  }

  match = /^(\w+) must be shorter than or equal to (\d+) characters$/i.exec(line);
  if (match) {
    return `The ${labelFor(match[1])} is too long.`;
  }

  match = /^(\w+) must be a valid iso 8601 date string$/i.exec(line);
  if (match) {
    return `Enter a valid date for ${labelFor(match[1])}.`;
  }

  match = /^(\w+) must be a uuid$/i.exec(line);
  if (match) {
    return `That ${labelFor(match[1])} is not valid.`;
  }

  match = /^(\w+) must be a number$/i.exec(line);
  if (match) {
    return `Enter a valid number for ${labelFor(match[1])}.`;
  }

  match = /^(startTime|endTime) must match /i.exec(line);
  if (match) {
    return `Enter ${labelFor(match[1])} as 24-hour time, for example 16:00.`;
  }

  match = /^(\w+) must match .+ regular expression$/i.exec(line);
  if (match) {
    return `The ${labelFor(match[1])} is not in the right format.`;
  }

  match = /^(startTime|endTime) must be a 24-hour time/i.exec(line);
  if (match) {
    return `Enter ${labelFor(match[1])} as 24-hour time, for example 16:00.`;
  }

  if (looksTechnical(line)) {
    return null;
  }
  return line;
}

function normalizeMessage(raw: unknown): string | null {
  if (typeof raw === 'string') {
    return raw.trim() || null;
  }
  if (Array.isArray(raw)) {
    return raw
      .filter((item): item is string => typeof item === 'string')
      .join(' ')
      .trim() || null;
  }
  if (raw && typeof raw === 'object' && 'message' in raw) {
    const nested = (raw as { message?: unknown }).message;
    return typeof nested === 'string' ? nested.trim() || null : null;
  }
  return null;
}

function friendlyKnown(text: string): string {
  const key = text.trim().toLowerCase().replace(/\s+/g, ' ');
  return KNOWN_MESSAGES[key] ?? text.trim();
}

function labelFor(field: string): string {
  return FIELD_LABELS[field] ?? field.replace(/([A-Z])/g, ' $1').toLowerCase().trim();
}

function looksTechnical(text: string): boolean {
  return /prisma|sqlstate|exception|econnrefused|etimedout|whitelist|class-validator|cannot read|undefined|stack|p\d{4}|regular expression|must match \//i.test(
    text,
  );
}
